// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import uuid from 'react-uuid';

// Components
import Container from '../../container/container';

// Hooks
import useOfficesPostType from '../../../hooks/useOfficesPostType';

// Utilities
import removeHttp from '../../../utilities/removeHttp';

// Styles
import OfficesStyles from './offices.styles';

export default function Offices({ layout }) {
  const isSSR = typeof window === 'undefined';

  const heading = layout?.heading;
  const text = layout?.text;

  const filter = layout?.filter;
  const displayMap = layout?.displayMap;
  const officeFilter = layout?.office;
  const markers = [];

  let offices = useOfficesPostType();

  if (filter) {
    offices = offices.filter((office) => office?.id === officeFilter?.id);
    const filteredOffice = offices[0];

    if (displayMap && filteredOffice) {
      const officeTitle = filteredOffice?.title;

      switch (officeTitle) {
        case 'Dubai':
          markers.push({
            latLng: [24.972881251655426, 55.41114574881417],
          });
          break;
        case 'London':
          markers.push({
            latLng: [51.509865, -0.118092],
          });
          break;
        case 'Los Angeles':
          markers.push({
            latLng: [34.32239233336645, -117.99977685235704],
          });
          break;
        case 'New Jersey':
          markers.push({
            latLng: [41.155984251344364, -74.75773765282038],
          });
          break;
        case 'New York City':
          markers.push({
            latLng: [41.00745972309386, -74.13148878262157],
          });
          break;
        case 'New York':
          markers.push({
            latLng: [41.00745972309386, -74.13148878262157],
          });
          break;
        case 'San Francisco':
          markers.push({
            latLng: [37.83711428601512, -121.755246535261],
          });
          break;
        default:
          break;
      }
    }
  }

  const ClientSideOnlyOfficeMap = React.lazy(
    () => import('./officeMap/officeMap'),
  );

  return (
    <OfficesStyles filtered={!!filter}>
      <Container>
        <h1 className="heading">{heading}</h1>
        <div className="text">{parse(text)}</div>
      </Container>
      <Container>
        <div className="offices">
          {offices &&
            offices.map((office) => (
              <div className="office" key={uuid()}>
                <div className="top">
                  <h3 className="location-name">
                    {office?.title} <span className="separator" />
                  </h3>
                </div>
                <div className="contact-wrap">
                  <a
                    href={`tel:${office?.offices?.phoneNumber.replaceAll(
                      ' ',
                      '',
                    )}`}
                    className="phone"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      fill="#A0988F"
                      className="icon"
                    >
                      <path d="M484.6 330.6C484.6 330.6 484.6 330.6 484.6 330.6l-101.8-43.66c-18.5-7.688-40.2-2.375-52.75 13.08l-33.14 40.47C244.2 311.8 200.3 267.9 171.6 215.2l40.52-33.19c15.67-12.92 20.83-34.16 12.84-52.84L181.4 27.37C172.7 7.279 150.8-3.737 129.6 1.154L35.17 23.06C14.47 27.78 0 45.9 0 67.12C0 312.4 199.6 512 444.9 512c21.23 0 39.41-14.44 44.17-35.13l21.8-94.47C515.7 361.1 504.7 339.3 484.6 330.6zM457.9 469.7c-1.375 5.969-6.844 10.31-12.98 10.31c-227.7 0-412.9-185.2-412.9-412.9c0-6.188 4.234-11.48 10.34-12.88l94.41-21.91c1-.2344 2-.3438 2.984-.3438c5.234 0 10.11 3.094 12.25 8.031l43.58 101.7C197.9 147.2 196.4 153.5 191.8 157.3L141.3 198.7C135.6 203.4 133.8 211.4 137.1 218.1c33.38 67.81 89.11 123.5 156.9 156.9c6.641 3.313 14.73 1.531 19.44-4.219l41.39-50.5c3.703-4.563 10.16-6.063 15.5-3.844l101.6 43.56c5.906 2.563 9.156 8.969 7.719 15.22L457.9 469.7z" />
                    </svg>
                    {office?.offices?.phoneNumber}
                  </a>
                  <a
                    href={`mailto:${office?.offices?.emailAddress}`}
                    className="email"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      fill="#A0988F"
                      className="icon"
                    >
                      <path d="M0 128C0 92.65 28.65 64 64 64H448C483.3 64 512 92.65 512 128V384C512 419.3 483.3 448 448 448H64C28.65 448 0 419.3 0 384V128zM32 128V167.9L227.6 311.3C244.5 323.7 267.5 323.7 284.4 311.3L480 167.9V128C480 110.3 465.7 96 448 96H63.1C46.33 96 31.1 110.3 31.1 128H32zM32 207.6V384C32 401.7 46.33 416 64 416H448C465.7 416 480 401.7 480 384V207.6L303.3 337.1C275.1 357.8 236.9 357.8 208.7 337.1L32 207.6z" />
                    </svg>
                    {office?.offices?.emailAddress}
                  </a>
                  <a
                    href={office?.offices?.website}
                    target="_blank"
                    rel="noreferrer"
                    className="website"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                      fill="#A0988F"
                      className="icon"
                    >
                      <path d="M0 96C0 60.65 28.65 32 64 32H448C483.3 32 512 60.65 512 96V416C512 451.3 483.3 480 448 480H64C28.65 480 0 451.3 0 416V96zM160 128H480V96C480 78.33 465.7 64 448 64H160V128zM128 64H64C46.33 64 32 78.33 32 96V128H128V64zM32 160V416C32 433.7 46.33 448 64 448H448C465.7 448 480 433.7 480 416V160H32z" />
                    </svg>
                    {removeHttp(office?.offices?.website)}
                  </a>
                </div>
                <div className="address-wrap">
                  {office?.offices?.address && (
                    <ul className="address">
                      {office?.offices?.address.map((line) => (
                        <li className="line" key={uuid()}>
                          {line?.line}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}

          {!!isSSR && markers.length && (
            <React.Suspense fallback={<div />}>
              <ClientSideOnlyOfficeMap markers={markers} />
            </React.Suspense>
          )}
        </div>
      </Container>
    </OfficesStyles>
  );
}

Offices.propTypes = {
  layout: PropTypes.shape({
    heading: PropTypes.string,
    text: PropTypes.string,
    filter: PropTypes.bool,
    displayMap: PropTypes.bool,
    office: PropTypes.shape({}),
  }),
};

Offices.defaultProps = {
  layout: { filter: true, displayMap: false },
};
